import React from "react";
import { useForm } from "react-hook-form";
import Button from "components/ui/Button";

export default function AgencyPartyEdit({ initialData = {}, onSubmit }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      company: initialData.company || "",
      abn: initialData.abn || "",
      gst: initialData.gst || "No",
      address: initialData.address || "",
      phone: initialData.phone || "",
      email: initialData.email || "",
      licenceNumber: initialData.licenceNumber || "",
      signatoryName: initialData.signatoryName || "",
      signature: initialData.signature || "",
    },
  });

  const submitForm = (data) => {
    const transformedData = {
      ...data,
      company: data.company.trim(),
      abn: data.abn.trim(),
      address: data.address.trim(),
      phone: data.phone.trim(),
      email: data.email.trim().toLowerCase(),
      licenceNumber: data.licenceNumber.trim(),
      signatoryName: data.signatoryName.trim(),
    };
    onSubmit(transformedData);
  };

  return (
    <form onSubmit={handleSubmit(submitForm)} className="space-y-6">
      {/* Company */}
      <div className="space-y-2">
        <label htmlFor="company" className="form-label">
          Company *
        </label>
        <input
          type="text"
          id="company"
          {...register("company", { required: "Company is required" })}
          className={`form-input border ${errors.company ? "border-red-500" : "border-mediumgray"}`}
          placeholder="e.g., Ausrealty Pty Ltd"
        />
        {errors.company && (
          <p className="form-error-message">{errors.company.message}</p>
        )}
      </div>

      {/* ABN + GST */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="abn" className="form-label">
            ABN *
          </label>
          <input
            type="text"
            id="abn"
            {...register("abn", { required: "ABN is required" })}
            className={`form-input border ${errors.abn ? "border-red-500" : "border-mediumgray"}`}
            placeholder="e.g., 12 345 678 901"
          />
          {errors.abn && (
            <p className="form-error-message">{errors.abn.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="gst" className="form-label">
            GST Registered *
          </label>
          <select
            id="gst"
            {...register("gst", { required: "GST selection is required" })}
            className={`form-input border ${errors.gst ? "border-red-500" : "border-mediumgray"}`}
          >
            <option value="No">No</option>
            <option value="Yes">Yes</option>
          </select>
          {errors.gst && (
            <p className="form-error-message">{errors.gst.message}</p>
          )}
        </div>
      </div>

      {/* Licence Number */}
      <div className="space-y-2">
        <label htmlFor="licenceNumber" className="form-label">
          Licence Number *
        </label>
        <input
          type="text"
          id="licenceNumber"
          {...register("licenceNumber", {
            required: "Licence number is required",
          })}
          className={`form-input border ${errors.licenceNumber ? "border-red-500" : "border-mediumgray"}`}
          placeholder="e.g., 1234567"
        />
        {errors.licenceNumber && (
          <p className="form-error-message">{errors.licenceNumber.message}</p>
        )}
        <p className="text-sm text-darkgray">
          The real-estate licence number for this party.
        </p>
      </div>

      {/* Address */}
      <div className="space-y-2">
        <label htmlFor="address" className="form-label">
          Address *
        </label>
        <input
          type="text"
          id="address"
          {...register("address", { required: "Address is required" })}
          className={`form-input border ${errors.address ? "border-red-500" : "border-mediumgray"}`}
          placeholder="e.g., 123 Main St, Sydney NSW 2000"
        />
        {errors.address && (
          <p className="form-error-message">{errors.address.message}</p>
        )}
      </div>

      {/* Phone + Email */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="phone" className="form-label">
            Phone *
          </label>
          <input
            type="text"
            id="phone"
            {...register("phone", { required: "Phone is required" })}
            className={`form-input border ${errors.phone ? "border-red-500" : "border-mediumgray"}`}
            placeholder="e.g., 02 9000 0000"
          />
          {errors.phone && (
            <p className="form-error-message">{errors.phone.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="form-label">
            Email *
          </label>
          <input
            type="email"
            id="email"
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Enter a valid email address",
              },
            })}
            className={`form-input border ${errors.email ? "border-red-500" : "border-mediumgray"}`}
            placeholder="e.g., admin@company.com.au"
          />
          {errors.email && (
            <p className="form-error-message">{errors.email.message}</p>
          )}
        </div>
      </div>

      {/* Signatory details — used only when this party signs
          (joint / trust account licensee) */}
      <div className="space-y-4 pt-2 border-t border-mediumgray">
        <div>
          <label className="form-label">Signatory Details</label>
          <p className="text-sm text-darkgray">
            Used only when this party signs an agreement as a joint or trust
            account licensee.
          </p>
        </div>

        <div className="space-y-2">
          <label htmlFor="signatoryName" className="form-label">
            Signatory Name
          </label>
          <input
            type="text"
            id="signatoryName"
            {...register("signatoryName")}
            className="form-input border border-mediumgray"
            placeholder="e.g., John Smith"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="signature" className="form-label">
            Signature
          </label>
          <input
            type="text"
            id="signature"
            {...register("signature")}
            className="form-input border border-mediumgray"
            placeholder="Signature reference / URL"
          />
          <p className="text-sm text-darkgray">
            Signature image URL or reference, if stored.
          </p>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end mt-6 pt-4 border-t border-mediumgray">
        <Button type="submit" className="black-button">
          {initialData._id ? "Update Agency Party" : "Create Agency Party"}
        </Button>
      </div>
    </form>
  );
}
